/**
 * ProvisionAccessModal — Provisionamento de access request APPROVED (P2).
 *
 * 3 etapas reais:
 *   1. Configurar — coleta de INTENÇÃO (destino + responsável). Zero write.
 *   2. Revisar   — exibe LITERALMENTE o plano retornado por /provision/preview.
 *                  O React não interpreta a operação nem replica a máquina.
 *   3. Confirmar — POST /provision (bloqueado contra duplo clique).
 *
 * Regras de segurança:
 *  - Qualquer mudança de intenção (destino, clube, responsável, e-mail)
 *    INVALIDA o preview (preview=null) e obriga novo preview antes de
 *    habilitar Confirmar.
 *  - keep_existing só habilita se o provisioning-context mostrar ≥1 GESTOR
 *    ativo (o backend revalida de qualquer forma).
 *  - Clube novo: dados do request são SOMENTE LEITURA (o backend cria a
 *    partir do commercial_access_request; override editável é feature futura
 *    com registro em activity).
 *  - Fechar o modal em qualquer etapa = zero write.
 *
 * Modos expostos nesta rodada: keep_existing e invite (existing_user fica
 * para depois — o backend já suporta).
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Building2, Search, CheckCircle2, AlertTriangle, ArrowLeft, X, Loader2, Mail, ShieldCheck } from 'lucide-react';
import { COLORS, STYLES } from './governanceTheme';
import {
  searchInstitutions, getProvisioningContext,
  previewProvision, provisionAccessRequest,
} from '../../services/accessRequestsService';

const L = {
  pt: {
    title: 'Provisionar acesso',
    stepConfigure: 'Configurar', stepReview: 'Revisar', stepDone: 'Concluído',
    requestSection: 'Solicitação',
    destSection: 'O clube já existe?',
    destExisting: 'Vincular a clube existente',
    destNew: 'Criar novo clube',
    clubLabel: 'Clube',
    clubSearchPlaceholder: 'Buscar clube pelo nome...',
    clubNoResults: 'Nenhum clube encontrado',
    gestoresLabel: 'Gestores atuais',
    gestoresNone: 'Este clube não possui GESTOR ativo',
    gestoresLoading: 'Carregando gestores...',
    newClubSummary: 'Novo clube a ser criado',
    newClubNote: 'Os dados vêm da solicitação. Alterações no nome/local serão uma funcionalidade futura, com registro de auditoria.',
    respSection: 'Responsável após provisionamento',
    respKeep: 'Manter gestores existentes',
    respKeepDisabled: 'Requer ao menos 1 GESTOR ativo no clube',
    respInvite: 'Convidar novo gestor',
    respInviteContact: (name) => `Convidar ${name || 'contato da solicitação'}`,
    respInviteOther: 'Convidar outro e-mail',
    inviteEmailLabel: 'E-mail do gestor',
    inviteNameLabel: 'Nome (opcional)',
    planSection: 'Plano comercial',
    planFromLead: (c) => `Plano solicitado no lead: ${c}`,
    planNoLead: 'Lead sem plano — sem override, o clube fica sem assinatura (legado)',
    planUseLead: 'Usar plano do lead',
    planNone: 'Sem plano (clube legado, sem assinatura)',
    planOverride: 'Definir/alterar plano',
    planReview: (c) => `Plano: ${c}`,
    planRespRole: (r) => `Responsável entrará como ${r}`,
    planSetupAdmin: 'com autoridade de configuração (setup admin)',
    cancel: 'Cancelar',
    back: 'Voltar',
    reviewCta: 'Revisar provisionamento',
    reviewTitle: 'Revisar provisionamento',
    willChange: 'Será alterado',
    noChange: 'Não será alterado',
    respAfter: 'Responsável após provisionamento',
    inviteWarning: (email) => `Será criado um convite GESTOR para ${email} · Validade: 30 dias`,
    grantWarning: (email) => `Será concedido acesso GESTOR a ${email}`,
    confirmNote: 'Esta ação vinculará/criará o clube e converterá a solicitação. Revise as alterações acima antes de continuar.',
    confirmCta: 'Confirmar provisionamento',
    provisioning: 'Provisionando...',
    previewLoading: 'Gerando preview...',
    doneTitle: 'Provisionamento concluído',
    doneConverted: 'Solicitação convertida',
    doneClub: (n) => `Clube: ${n}`,
    doneKeep: 'Responsável: gestor existente mantido',
    doneInvite: (e) => `Convite GESTOR enviado para ${e}`,
    close: 'Fechar',
    alreadyConverted: 'Esta solicitação já foi convertida anteriormente (operação idempotente — nada foi alterado).',
    errPreview: 'Erro ao gerar preview',
    errProvision: 'Erro ao provisionar',
  },
  en: {
    title: 'Provision access',
    stepConfigure: 'Configure', stepReview: 'Review', stepDone: 'Done',
    requestSection: 'Request',
    destSection: 'Does the club already exist?',
    destExisting: 'Link to existing club',
    destNew: 'Create new club',
    clubLabel: 'Club',
    clubSearchPlaceholder: 'Search club by name...',
    clubNoResults: 'No clubs found',
    gestoresLabel: 'Current managers',
    gestoresNone: 'This club has no active GESTOR',
    gestoresLoading: 'Loading managers...',
    newClubSummary: 'New club to be created',
    newClubNote: 'Data comes from the request. Name/location overrides are a future feature with audit trail.',
    respSection: 'Responsible after provisioning',
    respKeep: 'Keep existing managers',
    respKeepDisabled: 'Requires at least 1 active GESTOR in the club',
    respInvite: 'Invite new manager',
    respInviteContact: (name) => `Invite ${name || 'request contact'}`,
    respInviteOther: 'Invite another e-mail',
    inviteEmailLabel: 'Manager e-mail',
    inviteNameLabel: 'Name (optional)',
    planSection: 'Commercial plan',
    planFromLead: (c) => `Plan requested in the lead: ${c}`,
    planNoLead: 'Lead without plan — without an override the club stays legacy (no subscription)',
    planUseLead: 'Use lead plan',
    planNone: 'No plan (legacy club, no subscription)',
    planOverride: 'Set/override plan',
    planReview: (c) => `Plan: ${c}`,
    planRespRole: (r) => `Responsible will join as ${r}`,
    planSetupAdmin: 'with setup authority (setup admin)',
    cancel: 'Cancel',
    back: 'Back',
    reviewCta: 'Review provisioning',
    reviewTitle: 'Review provisioning',
    willChange: 'Will change',
    noChange: 'Will not change',
    respAfter: 'Responsible after provisioning',
    inviteWarning: (email) => `A GESTOR invite will be created for ${email} · Valid for 30 days`,
    grantWarning: (email) => `GESTOR access will be granted to ${email}`,
    confirmNote: 'This action will link/create the club and convert the request. Review the changes above before continuing.',
    confirmCta: 'Confirm provisioning',
    provisioning: 'Provisioning...',
    previewLoading: 'Generating preview...',
    doneTitle: 'Provisioning complete',
    doneConverted: 'Request converted',
    doneClub: (n) => `Club: ${n}`,
    doneKeep: 'Responsible: existing manager kept',
    doneInvite: (e) => `GESTOR invite sent to ${e}`,
    close: 'Close',
    alreadyConverted: 'This request was already converted (idempotent operation — nothing changed).',
    errPreview: 'Error generating preview',
    errProvision: 'Error provisioning',
  },
};

const Radio = ({ checked, disabled, onChange, children, hint }) => (
  <label className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
    checked ? 'border-emerald-600 bg-emerald-50' : 'border-gray-200 hover:bg-gray-50'
  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
    <input type="radio" checked={checked} disabled={disabled} onChange={onChange} className="mt-1" />
    <span className="text-sm">
      <span className="font-semibold text-gray-800">{children}</span>
      {hint && <span className="block text-xs text-gray-500 mt-0.5">{hint}</span>}
    </span>
  </label>
);

const ProvisionAccessModal = ({ request, lang = 'pt', onClose, onProvisioned }) => {
  const t = L[lang] || L.pt;
  const [step, setStep] = useState(1);

  // ── Intenção ──
  const [instMode, setInstMode] = useState('existing');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedInst, setSelectedInst] = useState(null);
  const [context, setContext] = useState(null); // { institution, active_gestores }
  const [ctxLoading, setCtxLoading] = useState(false);
  const [respMode, setRespMode] = useState(null); // 'keep_existing' | 'invite'
  const [inviteTarget, setInviteTarget] = useState('contact'); // 'contact' | 'other'
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');

  // ── Plano (P3.2) ──
  // 'lead' = usa plan_code do request (não envia override);
  // 'none' = clube legado sem assinatura; senão = plan_code de override.
  const PLAN_CODES = ['basic_s', 'basic_m', 'basic_l', 'basic_xl',
    'basic_plus_s', 'basic_plus_m', 'basic_plus_l', 'basic_plus_xl',
    'pro_s', 'pro_m', 'pro_l', 'pro_xl',
    'elite_s', 'elite_m', 'elite_l', 'elite_xl'];
  const [planChoice, setPlanChoice] = useState('lead');

  // ── Preview / provision ──
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const searchTimer = useRef(null);

  // Regra 13: QUALQUER mudança de intenção invalida o preview.
  const invalidatePreview = useCallback(() => {
    setPreview(null);
    setError(null);
    if (step > 1) setStep(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Busca server-side (debounced) — endpoint founder-only.
  useEffect(() => {
    if (instMode !== 'existing') return;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try { setResults(await searchInstitutions(query, 20)); }
      catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [query, instMode]);

  // Selecionou clube → carrega provisioning-context (gestores).
  const selectInstitution = async (inst) => {
    setSelectedInst(inst);
    setContext(null);
    setRespMode(null);
    invalidatePreview(); // Regra 12: outra instituição invalida preview anterior
    setCtxLoading(true);
    try { setContext(await getProvisioningContext(inst.institution_id)); }
    catch { setContext({ institution: inst, active_gestores: [] }); }
    finally { setCtxLoading(false); }
  };

  const gestores = context?.active_gestores || [];
  const keepAllowed = instMode === 'existing' && gestores.length > 0;

  const effectiveEmail = inviteTarget === 'contact' ? (request.email || '') : inviteEmail.trim();
  const effectiveName = inviteTarget === 'contact' ? (request.contact_name || '') : inviteName.trim();

  const buildPayload = () => {
    const p = { institution_mode: instMode, responsible_mode: respMode };
    if (instMode === 'existing') p.institution_id = selectedInst?.institution_id;
    if (respMode === 'invite') {
      p.responsible_email = effectiveEmail;
      if (effectiveName) p.responsible_name = effectiveName;
    }
    // 'lead'/'none' → sem override (backend usa o plan_code do request, se houver)
    if (planChoice !== 'lead' && planChoice !== 'none') p.plan_code = planChoice;
    return p;
  };

  const canReview = !!respMode &&
    (instMode === 'new' || !!selectedInst) &&
    (respMode !== 'invite' || (effectiveEmail && effectiveEmail.includes('@')));

  const doPreview = async () => {
    setPreviewLoading(true);
    setError(null);
    try {
      const plan = await previewProvision(request.request_id, buildPayload());
      setPreview(plan);
      setStep(2);
    } catch (err) {
      setError(`${t.errPreview}: ${err.response?.data?.error || err.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const doProvision = async () => {
    if (provisioning) return; // Regra 14: anti duplo clique
    setProvisioning(true);
    setError(null);
    try {
      const res = await provisionAccessRequest(request.request_id, buildPayload());
      setResult(res);
      setStep(3);
      onProvisioned?.(request.request_id, res);
    } catch (err) {
      setError(`${t.errProvision}: ${err.response?.data?.error || err.message}`);
    } finally {
      setProvisioning(false);
    }
  };

  const place = [request.city, request.state].filter(Boolean).join(', ');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className={STYLES.card + ' w-full max-w-lg p-0 max-h-[90vh] flex flex-col'}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5" style={{ color: COLORS.primary || '#064e3b' }} />
            <h3 className="text-lg font-bold text-gray-900">{t.title}</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label={t.close}>
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center gap-2 px-6 pt-3 text-xs font-bold">
          {[t.stepConfigure, t.stepReview, t.stepDone].map((s, i) => (
            <React.Fragment key={s}>
              {i > 0 && <span className="text-gray-300">›</span>}
              <span className={step === i + 1 ? 'text-emerald-700' : step > i + 1 ? 'text-gray-400' : 'text-gray-300'}>
                {i + 1}. {s}
              </span>
            </React.Fragment>
          ))}
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {/* Solicitação (sempre visível) */}
          <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
            <p className="text-xs font-bold text-gray-500 uppercase mb-1">{t.requestSection} #{request.request_id}</p>
            <p className="font-bold text-gray-900">{request.club_name}</p>
            <p className="text-gray-600">{request.contact_name}{request.email ? ` · ${request.email}` : ''}</p>
            {place && <p className="text-gray-500 text-xs">{place}</p>}
            <p className="text-xs mt-1" style={{ color: request.plan_code ? '#065f46' : '#92400e' }}>
              {request.plan_code ? t.planFromLead(request.plan_code) : t.planNoLead}
            </p>
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />{error}
            </div>
          )}

          {/* ── STEP 1: Configurar ── */}
          {step === 1 && (
            <>
              <p className="text-sm font-bold text-gray-800 mb-2">{t.destSection}</p>
              <div className="space-y-2 mb-4">
                <Radio checked={instMode === 'existing'}
                  onChange={() => { setInstMode('existing'); setRespMode(null); invalidatePreview(); }}>
                  {t.destExisting}
                </Radio>
                <Radio checked={instMode === 'new'}
                  onChange={() => { setInstMode('new'); setSelectedInst(null); setContext(null); setRespMode(null); invalidatePreview(); }}>
                  {t.destNew}
                </Radio>
              </div>

              {instMode === 'existing' && (
                <div className="mb-4">
                  <p className="text-xs font-bold text-gray-500 uppercase mb-1">{t.clubLabel}</p>
                  {selectedInst ? (
                    <div className="flex items-center justify-between border border-emerald-200 bg-emerald-50 rounded-lg p-3">
                      <div>
                        <p className="font-bold text-gray-900 text-sm">{selectedInst.name}</p>
                        <p className="text-xs text-gray-500">{[selectedInst.city, selectedInst.country].filter(Boolean).join(', ')}</p>
                      </div>
                      <button onClick={() => { setSelectedInst(null); setContext(null); setRespMode(null); invalidatePreview(); }}
                        className="text-xs font-bold text-gray-500 hover:text-gray-700">
                        {t.back}
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                        <input value={query} onChange={(e) => setQuery(e.target.value)}
                          placeholder={t.clubSearchPlaceholder}
                          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm" />
                      </div>
                      <div className="mt-1 border border-gray-100 rounded-lg divide-y divide-gray-50 max-h-40 overflow-y-auto">
                        {searching && <p className="p-2 text-xs text-gray-400">...</p>}
                        {!searching && results.length === 0 && <p className="p-2 text-xs text-gray-400">{t.clubNoResults}</p>}
                        {!searching && results.map(r => (
                          <button key={r.institution_id} onClick={() => selectInstitution(r)}
                            className="w-full text-left p-2 hover:bg-gray-50 text-sm">
                            <span className="font-semibold text-gray-800">{r.name}</span>
                            <span className="text-xs text-gray-400 ml-2">{[r.city, r.country].filter(Boolean).join(', ')}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  {selectedInst && (
                    <div className="mt-3">
                      <p className="text-xs font-bold text-gray-500 uppercase mb-1">{t.gestoresLabel}</p>
                      {ctxLoading ? (
                        <p className="text-xs text-gray-400">{t.gestoresLoading}</p>
                      ) : gestores.length > 0 ? (
                        <ul className="text-sm text-gray-700 space-y-0.5">
                          {gestores.map(g => (
                            <li key={g.user_id} className="flex items-center gap-1.5">
                              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                              {g.name ? `${g.name} · ` : ''}{g.email}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-amber-700 flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" />{t.gestoresNone}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {instMode === 'new' && (
                <div className="mb-4 border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <p className="text-xs font-bold text-gray-500 uppercase mb-1">{t.newClubSummary}</p>
                  <p className="font-bold text-gray-900 text-sm">{request.club_name}</p>
                  <p className="text-xs text-gray-500">
                    {[request.city, request.state, request.country_code].filter(Boolean).join(', ') || '—'}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-2">{t.newClubNote}</p>
                </div>
              )}

              {/* Responsável */}
              {(instMode === 'new' || selectedInst) && !ctxLoading && (
                <>
                  <p className="text-sm font-bold text-gray-800 mb-2">{t.planSection}</p>
                  <select
                    value={planChoice}
                    onChange={(e) => { setPlanChoice(e.target.value); invalidatePreview(); }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-4"
                  >
                    <option value="lead">{request.plan_code ? `${t.planUseLead} (${request.plan_code})` : t.planNone}</option>
                    {PLAN_CODES.map((c) => <option key={c} value={c}>{t.planOverride}: {c}</option>)}
                  </select>

                  <p className="text-sm font-bold text-gray-800 mb-2">{t.respSection}</p>
                  <div className="space-y-2">
                    {instMode === 'existing' && (
                      <Radio checked={respMode === 'keep_existing'} disabled={!keepAllowed}
                        hint={!keepAllowed ? t.respKeepDisabled : undefined}
                        onChange={() => { setRespMode('keep_existing'); invalidatePreview(); }}>
                        {t.respKeep}
                      </Radio>
                    )}
                    <Radio checked={respMode === 'invite'}
                      onChange={() => { setRespMode('invite'); invalidatePreview(); }}>
                      {t.respInvite}
                    </Radio>
                  </div>

                  {respMode === 'invite' && (
                    <div className="mt-3 pl-3 border-l-2 border-emerald-100 space-y-2">
                      <Radio checked={inviteTarget === 'contact'}
                        hint={request.email}
                        onChange={() => { setInviteTarget('contact'); invalidatePreview(); }}>
                        {t.respInviteContact(request.contact_name)}
                      </Radio>
                      <Radio checked={inviteTarget === 'other'}
                        onChange={() => { setInviteTarget('other'); invalidatePreview(); }}>
                        {t.respInviteOther}
                      </Radio>
                      {inviteTarget === 'other' && (
                        <div className="space-y-2 pt-1">
                          <input value={inviteEmail}
                            onChange={(e) => { setInviteEmail(e.target.value); invalidatePreview(); }}
                            placeholder={t.inviteEmailLabel} type="email"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                          <input value={inviteName}
                            onChange={(e) => { setInviteName(e.target.value); invalidatePreview(); }}
                            placeholder={t.inviteNameLabel}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ── STEP 2: Revisar (plano LITERAL do backend) ── */}
          {step === 2 && preview && (
            <>
              <h4 className="text-base font-bold text-gray-900 mb-3">{t.reviewTitle}</h4>

              <p className="text-xs font-bold text-emerald-700 uppercase mb-1">{t.willChange}</p>
              <ul className="mb-4 space-y-1">
                {(preview.writes || []).map((w, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-sm text-gray-800">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />{w}
                  </li>
                ))}
              </ul>

              <p className="text-xs font-bold text-gray-400 uppercase mb-1">{t.noChange}</p>
              <ul className="mb-4 space-y-0.5">
                {(preview.no_changes || []).map((n, i) => (
                  <li key={i} className="text-sm text-gray-500">— {n}</li>
                ))}
              </ul>

              {/* Plano comercial (LITERAL do preview) */}
              {preview.commercial_plan && preview.commercial_plan.plan_code && (
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 mb-3 text-sm">
                  <p className="text-xs font-bold text-emerald-700 uppercase mb-1">{t.planSection}</p>
                  <p className="text-gray-800 font-semibold">
                    {t.planReview(preview.commercial_plan.plan_name || preview.commercial_plan.plan_code)}
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {t.planRespRole(preview.commercial_plan.responsible_role)}
                    {preview.commercial_plan.grant_setup_admin ? ` · ${t.planSetupAdmin}` : ''}
                  </p>
                </div>
              )}

              {/* Responsável após provisionamento */}
              <div className="bg-gray-50 rounded-lg p-3 mb-3 text-sm">
                <p className="text-xs font-bold text-gray-500 uppercase mb-1">{t.respAfter}</p>
                {preview.responsible?.mode === 'keep_existing' && (
                  <ul>
                    {(preview.responsible.existing_gestores || []).map(g => (
                      <li key={g.user_id} className="text-gray-800">{g.email}</li>
                    ))}
                  </ul>
                )}
                {preview.responsible?.mode === 'invite' && (
                  <div className="flex items-start gap-2 text-amber-800">
                    <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {t.inviteWarning(preview.responsible.email)}
                  </div>
                )}
                {preview.responsible?.mode === 'existing_user' && (
                  <div className="flex items-start gap-2 text-amber-800">
                    <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {t.grantWarning(preview.responsible.email)}
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-500 flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-500" />
                {t.confirmNote}
              </p>
            </>
          )}

          {/* ── STEP 3: Concluído ── */}
          {step === 3 && result && (
            <div className="text-center py-4">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <h4 className="text-lg font-bold text-gray-900 mb-3">{t.doneTitle}</h4>
              {result.already_converted ? (
                <p className="text-sm text-gray-600">{t.alreadyConverted}</p>
              ) : (
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>✓ {t.doneConverted}</li>
                  <li>✓ {t.doneClub(result.institution?.name || '')}</li>
                  {result.responsible?.mode === 'keep_existing' && <li>✓ {t.doneKeep}</li>}
                  {result.responsible?.invited && <li>✓ {t.doneInvite(effectiveEmail)}</li>}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          {step === 1 && (
            <>
              <button onClick={onClose} className={STYLES.buttonSecondary}>{t.cancel}</button>
              <button onClick={doPreview} disabled={!canReview || previewLoading}
                className="px-4 py-2 text-sm font-bold text-white rounded-lg disabled:opacity-40"
                style={{ backgroundColor: '#064e3b' }}>
                {previewLoading ? t.previewLoading : t.reviewCta}
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <button onClick={() => { setStep(1); }} disabled={provisioning}
                className={STYLES.buttonSecondary + ' flex items-center gap-1'}>
                <ArrowLeft className="w-3.5 h-3.5" />{t.back}
              </button>
              <button onClick={doProvision} disabled={provisioning || !preview}
                className="px-4 py-2 text-sm font-bold text-white rounded-lg disabled:opacity-40 flex items-center gap-2"
                style={{ backgroundColor: '#064e3b' }}>
                {provisioning && <Loader2 className="w-4 h-4 animate-spin" />}
                {provisioning ? t.provisioning : t.confirmCta}
              </button>
            </>
          )}
          {step === 3 && (
            <button onClick={onClose} className="px-4 py-2 text-sm font-bold text-white rounded-lg"
              style={{ backgroundColor: '#064e3b' }}>
              {t.close}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProvisionAccessModal;
